import { describeError } from '@cf-shared/error-describe';

/**
 * #5445: what to do when Paddle's price preview fails.
 *
 * The subscribe page asks Paddle.js `PricePreview` for the plan prices it shows in the plan
 * picker. Two facts drive this module:
 *
 *   1. Paddle.js never rejects with an `Error`. On a network failure it rejects with
 *      `{ error: { type: 'network_error', code: 'network_error', detail } }`, and on an HTTP
 *      error with Paddle's raw JSON body `{ error: { type, code, detail, errors? }, meta:
 *      { request_id } }`. Reading `.message` off it gives `undefined`, which is why every
 *      report before this fix had an empty context.
 *
 *   2. Paddle answers 400 when the country we pass is one it does not sell to
 *      (`address_location_not_allowed` — Belarus, Russia, Iran, ...) or is not in its list at
 *      all (`bad_request` naming `address.country_code`). Asking again without a country is
 *      the same request the page makes for a visitor whose country is unknown, and it works.
 *
 * Pure, so it can be unit-tested under node; `PaddleCheckoutService` wires it to Paddle.js and
 * the logging service. See design-docs/paddle-integration.md, "Price preview failures".
 */

/** Paddle's error code for a country it refuses to sell to. */
const CODE_LOCATION_NOT_ALLOWED = 'address_location_not_allowed';
/** Paddle's error code for a request that failed schema validation. */
const CODE_BAD_REQUEST = 'bad_request';
/** Paddle.js's own code when fetch itself threw. */
const CODE_NETWORK_ERROR = 'network_error';
/** The request field Paddle names when the country code is not in its list. */
const FIELD_COUNTRY_CODE = 'address.country_code';

/** The info report filed when the preview never reached Paddle. */
export const kPricePreviewNetworkErrorReport = 'PaddlePricePreviewNetworkError';

/** The parts of a failed price preview worth recording. */
export interface PricePreviewFailure {
  /** Paddle's error type, e.g. `request_error`, `api_error`, `network_error`. */
  type?: string;
  /** Paddle's error code, e.g. `address_location_not_allowed`. */
  code?: string;
  /** Paddle's human-readable detail, or a description of a non-Paddle rejection. */
  detail?: string;
  /** Paddle's request id, which Paddle support can look up. Absent on a network failure. */
  requestId?: string;
  /** The request fields a validation error names (empty when none). */
  fields: string[];
}

export enum PricePreviewFailureKind {
  /** The request never reached Paddle (offline, ad blocker, in-app browser). */
  Network = 'network',
  /** Paddle refused the country we sent; asking without it can work. */
  CountryRejected = 'country-rejected',
  /** Anything else — a real problem worth a person's attention. */
  Other = 'other',
}

/** Where the outcome is reported. `info` = expected or already repaired; `problem` = unresolved. */
export interface PricePreviewReporter {
  info(name: string, context: Record<string, unknown>): void;
  problem(name: string, context: Record<string, unknown>): void;
}

/** Read Paddle's fields out of whatever PricePreview rejected with. Never throws. */
export function describePricePreviewFailure(err: unknown): PricePreviewFailure {
  const root = asRecord(err);
  const error = asRecord(root?.['error']);
  const meta = asRecord(root?.['meta']);
  // if the rejection is not Paddle's shape, keep a description so the report is not empty
  if (!error) {
    return { detail: describeError(err), fields: [] };
  }
  const errors = Array.isArray(error['errors']) ? error['errors'] : [];
  const fields = errors
    .map(e => asString(asRecord(e)?.['field']))
    .filter((f): f is string => f !== undefined);
  return {
    type: asString(error['type']),
    code: asString(error['code']),
    detail: asString(error['detail']),
    requestId: asString(meta?.['request_id']),
    fields,
  };
}

/** Decide what kind of failure this is. `countrySent` = the failed request carried a country. */
export function classifyPricePreviewFailure(failure: PricePreviewFailure, countrySent: boolean): PricePreviewFailureKind {
  if (failure.code === CODE_NETWORK_ERROR) {
    return PricePreviewFailureKind.Network;
  }
  const blamesCountry = failure.code === CODE_LOCATION_NOT_ALLOWED
    || (failure.code === CODE_BAD_REQUEST && failure.fields.includes(FIELD_COUNTRY_CODE));
  // if no country was sent, dropping it cannot help, so this is not a country rejection
  if (blamesCountry && countrySent) {
    return PricePreviewFailureKind.CountryRejected;
  }
  return PricePreviewFailureKind.Other;
}

/**
 * Run the price preview, retrying once without the country when Paddle refuses the country.
 * Returns the preview, or `undefined` when it could not be fetched (the caller then shows plans
 * without prices). Every failure is reported through `reporter` at the right tier.
 */
export async function previewWithCountryFallbackAsync<T>(
  fetchPreview: (countryCode?: string) => Promise<T>,
  countryCode: string | undefined,
  reporter: PricePreviewReporter,
): Promise<T | undefined> {
  let firstFailure: PricePreviewFailure;
  try {
    return await fetchPreview(countryCode);
  } catch (err) {
    firstFailure = describePricePreviewFailure(err);
  }

  const kind = classifyPricePreviewFailure(firstFailure, !!countryCode);
  if (kind !== PricePreviewFailureKind.CountryRejected) {
    reportFinalFailure(reporter, kind, firstFailure, { countryCode });
    return undefined;
  }

  // Paddle refused the country: ask again without it, as for a visitor with no known country
  try {
    const preview = await fetchPreview(undefined);
    reporter.info('PaddlePricePreviewCountryRejected', { countryCode, code: firstFailure.code, requestId: firstFailure.requestId });
    return preview;
  } catch (err) {
    const retryFailure = describePricePreviewFailure(err);
    const retryKind = classifyPricePreviewFailure(retryFailure, false);
    reportFinalFailure(reporter, retryKind, retryFailure, {
      countryCode,
      retriedWithoutCountry: true,
      countryRejectedCode: firstFailure.code,
    });
    return undefined;
  }
}

/** Report a failure the preview could not recover from, at the tier its kind deserves. */
function reportFinalFailure(
  reporter: PricePreviewReporter,
  kind: PricePreviewFailureKind,
  failure: PricePreviewFailure,
  extra: Record<string, unknown>,
): void {
  const context = { ...extra, type: failure.type, code: failure.code, detail: failure.detail, requestId: failure.requestId, fields: failure.fields };
  if (kind === PricePreviewFailureKind.Network) {
    // the request never reached Paddle (offline, ad blocker, in-app browser): expected, so info.
    // The Paddle API hosts are on the fetch wrapper's allowlist, so this is the only report of it.
    reporter.info(kPricePreviewNetworkErrorReport, context);
    return;
  }
  reporter.problem('PaddlePricePreviewFailed', context);
}

/** Narrow an unknown value to a plain object, or undefined when it is not one. */
function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

/** Narrow an unknown value to a string, or undefined when it is not one. */
function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
